import { useTriggerFindTargetReport } from '@/features/approving_invoice_reports/components/List/context/TriggerFindTargetReportContext';
import {
  clearListRestoreKeys,
  DRAWER_ID_PARAM,
  readListSnapshot,
  readReturnIntent,
} from '@/features/approving_invoice_reports/utils/listSnapshotStorage';
import { useKeepApprovalSearchConditionsFlag } from '@/hooks/feature_flag/useKeepApprovalSearchConditionsFlag';
import { useEffect, useRef } from 'react';

export type UseRestoreDrawerAfterReturnParams = {
  isFetching: boolean;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
};

/**
 * NA-5815 / NA-5831: after the list came back from a successful edit / approve /
 * reject (see useApprovingInvoiceReportsFormInitial), decide what the drawer
 * does once the first fetch has finished, then forget both keys.
 *
 *  - edit: the restored URL already carries the edited request's `id`, so the
 *    drawer reopens by itself; only fall back to page 1 if the page is empty.
 *  - approve / reject with a drawer: open the request now sitting at the saved
 *    row, reusing the existing "find next" flow.
 *  - approve / reject from a row link (no drawer): only fall back to page 1 if
 *    the page is empty.
 *
 * Must run inside TriggerFindTargetReportProvider.
 */
export const useRestoreDrawerAfterReturn = ({
  isFetching,
  onDrawerOpen,
  onDrawerClose,
}: UseRestoreDrawerAfterReturnParams): void => {
  const {
    setSelectedIndex,
    onTriggerFindTargetReport,
    onTriggerBackToPageOneIfNoData,
  } = useTriggerFindTargetReport();
  const enabled = useKeepApprovalSearchConditionsFlag();
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current || isFetching) return;
    doneRef.current = true;

    // Flag off: never touch sessionStorage, identical to before NA-5815.
    if (!enabled) return;

    const intent = readReturnIntent();
    const snapshot = readListSnapshot();
    clearListRestoreKeys();
    if (!intent || !snapshot) return;

    const hadDrawerOpen = new URLSearchParams(snapshot.search).has(
      DRAWER_ID_PARAM
    );
    if (!intent.shouldOpenNextTarget || !hadDrawerOpen) {
      onTriggerBackToPageOneIfNoData();
      return;
    }

    setSelectedIndex(snapshot.selectedIndex ?? 0);
    onTriggerFindTargetReport(
      (report) => onDrawerOpen(report.id),
      onDrawerClose
    );
  }, [
    enabled,
    isFetching,
    onDrawerOpen,
    onDrawerClose,
    setSelectedIndex,
    onTriggerFindTargetReport,
    onTriggerBackToPageOneIfNoData,
  ]);
};
